'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { useCookiebotConsent } from '@/hooks/use-cookiebot-consent';
import { createTrackingEventId, isDedicatedCapiPageViewPath } from '@/utils/capi-event';
import { postCapiPageView } from '@/utils/capi-tracking';
import { getInitialPageViewEventId } from '@/utils/page-view-event-id';

/** Delay before SPA navigation events fire (for testing). */
const SPA_NAV_DELAY_MS = 1000;

/**
 * Keeps GTM page_view and Meta CAPI PageView on the same `event_id` / `id`.
 *
 * Initial load: reuses `window.__pageViewEventId` seeded in layout `<head>` before GTM
 * so the automatic GA4 page_view can pick up the same id from the dataLayer.
 * SPA navigations: mints a fresh id and sends `virtual_page_view` + CAPI together.
 */
const GtmRouteChangeListener = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const hasMarketingConsent = useCookiebotConsent('marketing');
  const hasStatisticsConsent = useCookiebotConsent('statistics');

  const initialPagePathRef = useRef<string | null>(null);
  const gtmInitialSentRef = useRef(false);
  const capiInitialSentRef = useRef(false);

  useEffect(() => {
    const pagePath = search ? `${pathname}?${search}` : pathname;

    if (initialPagePathRef.current === null) {
      initialPagePathRef.current = pagePath;
    }

    const isInitialPage = pagePath === initialPagePathRef.current;
    const shouldTrackCapi = hasMarketingConsent && !isDedicatedCapiPageViewPath(pathname);
    const shouldSendGtm = Boolean(process.env.NEXT_PUBLIC_GTM_ID) && hasStatisticsConsent;

    const runTrack = () => {
      if (!shouldSendGtm && !shouldTrackCapi) {
        return;
      }

      const eventId = isInitialPage ? getInitialPageViewEventId() : createTrackingEventId();
      const sourceUrl = window.location.href;
      const pageLocation = `${window.location.origin}${pagePath}${window.location.hash}`;

      if (shouldSendGtm && (!isInitialPage || !gtmInitialSentRef.current)) {
        sendGTMEvent({
          event: isInitialPage ? 'page_view' : 'virtual_page_view',
          event_id: eventId,
          page_path: pagePath,
          page_location: pageLocation,
          page_title: document.title,
        });

        if (isInitialPage) {
          gtmInitialSentRef.current = true;
        }
      }

      if (shouldTrackCapi && (!isInitialPage || !capiInitialSentRef.current)) {
        void postCapiPageView({ id: eventId, sourceUrl });

        if (isInitialPage) {
          capiInitialSentRef.current = true;
        }
      }
    };

    if (isInitialPage) {
      runTrack();
      return;
    }

    const timer = setTimeout(runTrack, SPA_NAV_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasMarketingConsent, hasStatisticsConsent, pathname, search]);

  return null;
};

export default GtmRouteChangeListener;
